import {
  ApiDimension,
  ApiDimensionCategory,
  ApiSimpleDimensionLabel,
} from "@incendium/api";
import {
  Autocomplete,
  Box,
  Button,
  OutlinedTextFieldProps,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { StyledTooltip } from "Components/UI/StyledTooltip";
import {
  dimensionCategoryTitle,
  orderGroupedDimensionLabels,
} from "features/analytics";
import { useDimensionLabels } from "features/analytics/hooks/useDimensionLabels";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { useChartDescriptionDrawer } from "Providers/ChartDescriptionDrawerProvider";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

interface IDimensionSelectorProps extends Omit<
  OutlinedTextFieldProps,
  "onChange" | "variant"
> {
  dimension: ApiDimension | null;
  initialDimension?: ApiDimension;
  onChange: (e: SyntheticEvent, v: ApiSimpleDimensionLabel) => void;
  dimensions?: ApiDimension[];
  dimensionCategories?: ApiDimensionCategory[];
  noTooltip?: boolean;
}

// Popular dimensions shown at the top
const popularDimensions = [
  ApiDimension.DIMENSION_CHANNEL,
  ApiDimension.DIMENSION_SOURCE,
  ApiDimension.DIMENSION_INTERNAL_CAMPAIGN,
  ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
  ApiDimension.DIMENSION_LANDING_PAGE_PATH,
  ApiDimension.DIMENSION_DEVICE,
  ApiDimension.DIMENSION_BROWSER,
  ApiDimension.DIMENSION_PRODUCT_NAME,
  ApiDimension.DIMENSION_CITY,
  ApiDimension.DIMENSION_VISITOR_TYPE,
  ApiDimension.DIMENSION_CUSTOMER_TYPE,
];

function DimensionSelector({
  dimension,
  initialDimension,
  label,
  fullWidth,
  onChange,
  dimensions,
  dimensionCategories,
  noTooltip,
  ...rest
}: IDimensionSelectorProps) {
  const { openDrawer } = useChartDescriptionDrawer();
  const dimensionToName = useDimensionName();
  const { dimensionLabels, loading } = useDimensionLabels();
  const [selectedOption, setSelectedOption] = useState<
    ApiSimpleDimensionLabel | undefined
  >(undefined);

  const popularDimensionsLabels: ApiSimpleDimensionLabel[] = useMemo(() => {
    return dimensionLabels
      .filter((d) => popularDimensions.includes(d.dimension as ApiDimension))
      .map((d) => ({
        ...d,
        category: "Popular Dimensions" as ApiDimensionCategory,
      }));
  }, [dimensionLabels]);

  const filterDimensions = useCallback(
    (
      items: ApiSimpleDimensionLabel[],
      dimensions?: ApiDimension[],
      dimensionCategories?: ApiDimensionCategory[],
    ) => {
      return items.filter((item) => {
        const hasDimension = dimensions?.length
          ? dimensions.includes(item.dimension as ApiDimension)
          : false;
        const hasCategory = dimensionCategories?.length
          ? dimensionCategories.includes(item.category as ApiDimensionCategory)
          : false;

        if (dimensions?.length && dimensionCategories?.length) {
          return hasDimension || hasCategory;
        } else if (dimensions?.length) {
          return hasDimension;
        } else if (dimensionCategories?.length) {
          return hasCategory;
        }
        return true;
      });
    },
    [],
  );

  const parsedDimensions: ApiSimpleDimensionLabel[] = useMemo(() => {
    const sorted = orderGroupedDimensionLabels(dimensionLabels);
    const filtered = filterDimensions(sorted, dimensions, dimensionCategories);
    const arr = filtered.map((s) => ({
      ...s,
      category: dimensionCategoryTitle(
        s.category || ApiDimensionCategory.DIMENSION_CATEGORY_UNKNOWN,
      ) as ApiDimensionCategory,
    }));
    return [...popularDimensionsLabels, ...arr];
  }, [
    dimensionLabels,
    dimensions,
    dimensionCategories,
    popularDimensionsLabels,
    filterDimensions,
  ]);

  useEffect(() => {
    const idx = parsedDimensions.findIndex((f) => f.dimension === dimension);
    if (idx < 0) {
      return;
    }

    setSelectedOption({
      ...parsedDimensions[idx],
      label:
        parsedDimensions[idx].label !== ""
          ? parsedDimensions[idx].label
          : (parsedDimensions[idx].dimension as string),
    });
  }, [dimension, parsedDimensions]);

  const getOptionLabel = useCallback(
    (option: ApiSimpleDimensionLabel) => {
      if (!option || !option.dimension) return "";
      return dimensionToName(option.dimension as ApiDimension);
    },
    [dimensionToName],
  );

  const groupBy = useCallback(
    (option: ApiSimpleDimensionLabel) =>
      option.category || ApiDimensionCategory.DIMENSION_CATEGORY_UNKNOWN,
    [],
  );

  const isOptionEqualToValue = useCallback(
    (option: ApiSimpleDimensionLabel, value: ApiSimpleDimensionLabel) =>
      option.dimension === value.dimension,
    [],
  );

  return (
    <Autocomplete
      id="dimension-selector"
      size="small"
      onChange={onChange}
      loading={loading}
      value={
        selectedOption || {
          dimension: "" as ApiDimension,
          label: "",
        }
      }
      groupBy={groupBy}
      options={parsedDimensions}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      disableClearable
      filterSelectedOptions
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          label={label || "Select Dimension"}
          variant="outlined"
          size="small"
        />
      )}
      renderOption={
        noTooltip
          ? undefined
          : (props, option) => {
              return (
                <StyledTooltip
                  wide
                  title={
                    <>
                      <Stack px={2} py={1}>
                        <Typography variant="subtitle1" color={"inherit"}>
                          {dimensionToName(option.dimension as ApiDimension)}
                        </Typography>
                        <Typography variant="body1" color={"inherit"}>
                          {option.description || "No description available"}
                        </Typography>
                      </Stack>

                      <Box mt={1}>
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          onClick={() =>
                            openDrawer([option.dimension as ApiDimension], [])
                          }
                        >
                          Click to see full description
                        </Button>
                      </Box>
                    </>
                  }
                  placement="left"
                  arrow
                  enterDelay={500}
                >
                  <Box component="li" {...props}>
                    {dimensionToName(option.dimension as ApiDimension)}
                  </Box>
                </StyledTooltip>
              );
            }
      }
    />
  );
}

export default DimensionSelector;
